import React, { useState } from 'react';
import { FormEvent } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BookOpenCheckIcon,
  EyeIcon,
  EyeOffIcon,
  InfoIcon,
  LockIcon,
  MailIcon,
  ShieldCheckIcon,
  TimerResetIcon,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BrandMark } from './BrandMark';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import type { UserRole } from '../../lib/types';

type LoginPageProps = {
  onBack: () => void;
  onRegister: () => void;
};

const ROLE_PATH: Record<UserRole, string> = {
  superadmin: '/gov-panel',
  admin: '/dashboard',
  operator: '/dashboard',
  driver: '/driver-portal',
};

const startChecks = [
  ['01', 'Föregående vila', 'Dokumenteras i tidboken före transport.'],
  ['02', 'Dagens körpass', 'Få en samlad startpunkt för dagen.'],
  ['03', 'Underlag nära', 'Tidboken ska kunna visas vid kontroll.'],
];

export function LoginPage({ onBack, onRegister }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const ok = await login(email.trim(), password);
      if (!ok) {
        setError('Fel e-postadress eller lösenord. Försök igen.');
        return;
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        const role = (profile?.role as UserRole) ?? 'operator';
        navigate(ROLE_PATH[role] ?? '/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch {
      setError('Ett fel uppstod vid inloggningen. Försök igen.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="ivilotid-site min-h-screen bg-[#F4F8FA] text-[#0B1120]">
      <header className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-5 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={onBack}
          className="group inline-flex min-h-10 items-center gap-2 rounded-lg px-1 text-sm font-semibold text-slate-700 hover:text-[#007EAF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]"
        >
          <ArrowLeftIcon
            size={16}
            className="transition-transform group-hover:-translate-x-0.5"
            aria-hidden="true"
          />
          Tillbaka
        </button>
        <BrandMark compact />
      </header>

      <main
        tabIndex={-1}
        className="mx-auto grid max-w-6xl overflow-hidden border-y border-[#0B1120]/10 bg-white sm:my-5 sm:rounded-2xl sm:border sm:shadow-[0_10px_28px_rgba(11,17,32,0.06)] lg:grid-cols-[.96fr_1.04fr]"
      >
        <section className="flex px-5 py-8 sm:px-8 sm:py-9 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto w-full max-w-md"
          >
            <p className="text-xs font-bold uppercase tracking-[.12em] text-[#006D98]">
              Förarstart
            </p>
            <h1 className="mt-2 font-display text-3xl tracking-[-.04em]">Börja ditt körpass.</h1>
            <p className="mt-2 text-sm leading-6 text-slate-700">
              Logga in till en lugn startpunkt för passet, vilan och den samlade
              arbetsdagen.
            </p>
            <section
              aria-label="Inför körpasset"
              className="mt-5 rounded-xl border border-[#06B6D4]/25 bg-[#ECFEFF] p-3.5"
            >
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[.1em] text-[#006D98]">
                <TimerResetIcon size={15} aria-hidden="true" />
                Inför körpasset
              </div>
              <p className="mt-2 text-sm leading-6 text-[#164E63]">
                Personlig tidbok ska innehålla uppgift om närmast föregående
                dygnsvila innan en transport påbörjas. Normalt krävs minst 11
                timmars vila inom de senaste 24 timmarna. Vilan kan delas upp i
                två perioder, där en period ska vara minst 8 timmar.
              </p>
              <p id="login-prototype-note" className="mt-2 text-sm leading-6 text-[#164E63]">
                Tjänsten visar endast kontext och gör ingen juridisk
                kontroll.
              </p>
            </section>

            <form onSubmit={handleSubmit} className="mt-5 space-y-4" noValidate={false}>
              <label className="block text-sm font-semibold text-slate-700">
                E-postadress
                <span className="relative mt-1.5 block">
                  <MailIcon
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    size={17}
                    aria-hidden="true"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    placeholder="namn@foretag.se"
                    autoComplete="email"
                    name="email"
                    aria-describedby="login-prototype-note"
                    className="h-11 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-3 text-sm text-[#0B1120] outline-none placeholder:text-slate-500 focus:border-[#06B6D4] focus:ring-4 focus:ring-[#06B6D4]/15"
                  />
                </span>
              </label>
              <label className="block text-sm font-semibold text-slate-700">
                Lösenord
                <span className="relative mt-1.5 block">
                  <LockIcon
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    size={17}
                    aria-hidden="true"
                  />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    placeholder="Ange ditt lösenord"
                    autoComplete="current-password"
                    name="password"
                    aria-describedby="login-prototype-note"
                    className="h-11 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-11 text-sm text-[#0B1120] outline-none placeholder:text-slate-500 focus:border-[#06B6D4] focus:ring-4 focus:ring-[#06B6D4]/15"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-[#007EAF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]"
                    aria-label={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
                  >
                    {showPassword ? (
                      <EyeOffIcon size={17} aria-hidden="true" />
                    ) : (
                      <EyeIcon size={17} aria-hidden="true" />
                    )}
                  </button>
                </span>
              </label>
              <motion.button
                type="submit"
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.14 }}
                disabled={loading}
                className="group flex h-11 w-full items-center justify-center gap-2.5 rounded-lg bg-[#06B6D4] text-sm font-bold text-[#071A28] shadow-[0_5px_12px_rgba(6,182,212,.18)] hover:bg-[#22D3EE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007EAF] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#071A28]/30 border-t-[#071A28]" />
                    Loggar in…
                  </>
                ) : (
                  <>
                    Logga in
                    <ArrowRightIcon
                      size={17}
                      className="transition-transform group-hover:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </>
                )}
              </motion.button>
            </form>

            <AnimatePresence>
              {error ? (
                <motion.div
                  role="status"
                  aria-live="polite"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.22 }}
                  className="mt-4 flex gap-2.5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm leading-6 text-red-700"
                >
                  <InfoIcon className="mt-0.5 shrink-0" size={17} aria-hidden="true" />
                  <p>{error}</p>
                </motion.div>
              ) : null}
            </AnimatePresence>

            <p className="mt-5 text-center text-sm text-slate-600">
              Saknar du konto?{' '}
              <button
                type="button"
                onClick={onRegister}
                className="font-bold text-[#007EAF] hover:text-[#06B6D4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]"
              >
                Skapa ett
              </button>
            </p>
          </motion.div>
        </section>

        <aside className="relative bg-[#0B1120] px-5 py-8 text-white sm:px-8 sm:py-9 lg:px-10">
          <div className="absolute inset-x-0 top-0 h-px bg-[#06B6D4]" aria-hidden="true" />
          <div className="mx-auto max-w-md">
            <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-4">
              <BrandMark inverted />
              <span className="rounded-md border border-[#67E8F9]/35 px-2 py-1 text-xs font-bold uppercase tracking-[.07em] text-[#CFFAFE]">
                Förarstart
              </span>
            </div>
            <div className="mt-6">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[.1em] text-[#CFFAFE]">
                <ShieldCheckIcon size={14} aria-hidden="true" />
                Inför passet · iVilotid
              </div>
              <h2 className="mt-3 font-display text-3xl leading-[1.08] tracking-[-.04em]">
                Kontroll före vägen. Lugn under passet.
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                En genomtänkt plats för det som behöver vara nära när
                arbetsdagen går från förberedelse till transport.
              </p>
            </div>
            <ol className="mt-6 space-y-2" aria-label="Översikt över förarstart">
              {startChecks.map(([number, title, copy], index) => (
                <motion.li
                  key={number}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.24,
                    delay: 0.08 + index * 0.04,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="grid grid-cols-[28px_1fr] gap-2.5 rounded-lg border border-white/10 bg-white/[0.04] p-2.5"
                >
                  <span className="grid h-7 w-7 place-items-center rounded-md bg-[#102C3F] text-xs font-bold text-[#CFFAFE]">
                    {number}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-white">{title}</p>
                    <p className="mt-0.5 text-sm leading-5 text-slate-300">{copy}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
            <div className="mt-5 flex items-center gap-2 border-t border-white/10 pt-4 text-sm leading-6 text-slate-300">
              <BookOpenCheckIcon
                size={15}
                className="shrink-0 text-[#F5C400]"
                aria-hidden="true"
              />
              Tidboksuppgifter är viktiga vid start och ska kunna visas vid
              kontroll.
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
